'use client';

import { AnimatePresence } from 'framer-motion';
import { useNotificationStore, type ToastPosition } from '@/lib/stores/notification-store';
import { ToastComponent } from './toast';
import { cn } from '@/lib/utils';
import { Z_INDEX } from '@/lib/z-index';

// ============================================================================
// POSITION CONFIG
// ============================================================================

const POSITION_CLASSES: Record<ToastPosition, string> = {
  'top-left': 'top-4 left-4 items-start',
  'top-center': 'top-4 left-1/2 -translate-x-1/2 items-center',
  'top-right': 'top-4 right-4 items-end',
  'center-left': 'top-1/2 -translate-y-1/2 left-4 items-start',
  'center': 'top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 items-center',
  'center-right': 'top-1/2 -translate-y-1/2 right-4 items-end',
  'bottom-left': 'bottom-4 left-4 items-start',
  'bottom-center': 'bottom-4 left-1/2 -translate-x-1/2 items-center',
  'bottom-right': 'bottom-4 right-4 items-end',
};

// ============================================================================
// COMPONENT
// ============================================================================

export function ToastContainer() {
  const toasts = useNotificationStore((state) => state.toasts);

  // Group toasts by position
  const toastsByPosition = toasts.reduce(
    (acc, toast) => {
      const position = toast.position ?? 'bottom-right';
      if (!acc[position]) acc[position] = [];
      acc[position].push(toast);
      return acc;
    },
    {} as Record<ToastPosition, typeof toasts>
  );

  return (
    <>
      {Object.entries(toastsByPosition).map(([position, positionToasts]) => (
        <div
          key={position}
          className={cn(
            'pointer-events-none fixed flex flex-col gap-2 p-4',
            POSITION_CLASSES[position as ToastPosition],
            'max-h-screen overflow-hidden'
          )}
          style={{ zIndex: Z_INDEX.toast }}
        >
          <AnimatePresence mode="popLayout">
            {positionToasts.map((toast) => (
              <div key={toast.id} className="pointer-events-auto">
                <ToastComponent toast={toast} />
              </div>
            ))}
          </AnimatePresence>
        </div>
      ))}
    </>
  );
}
